import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';


const ContactForm = ({ onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    companySize: '',
    industry: '',
    useCase: '',
    monthlyVolume: '',
    timeline: '',
    budget: '',
    technicalRequirements: [],
    complianceNeeds: [],
    message: '',
    newsletter: false,
    terms: false
  });

  const [errors, setErrors] = useState({});

  const companySizeOptions = [
    { value: 'startup', label: 'Startup (1-10 employees)' },
    { value: 'small', label: 'Small Business (11-50 employees)' },
    { value: 'medium', label: 'Medium Business (51-200 employees)' },
    { value: 'large', label: 'Large Enterprise (201-1000 employees)' },
    { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
  ];

  const industryOptions = [
    { value: 'fintech', label: 'Financial Technology' },
    { value: 'healthcare', label: 'Healthcare & Medical' },
    { value: 'ecommerce', label: 'E-commerce & Retail' },
    { value: 'education', label: 'Education & EdTech' },
    { value: 'gaming', label: 'Gaming & Entertainment' },
    { value: 'saas', label: 'SaaS & Software' },
    { value: 'logistics', label: 'Logistics & Transportation' },
    { value: 'other', label: 'Other' }
  ];

  const useCaseOptions = [
    { value: 'customer-support', label: 'Customer Support Chat' },
    { value: 'team-collaboration', label: 'Team Collaboration' },
    { value: 'social-messaging', label: 'Social Messaging Platform' },
    { value: 'marketplace', label: 'Marketplace Communication' },
    { value: 'gaming-chat', label: 'Gaming & Live Chat' },
    { value: 'notifications', label: 'Push Notifications' },
    { value: 'video-calling', label: 'Video & Voice Calling' },
    { value: 'other', label: 'Other Use Case' }
  ];

  const volumeOptions = [
    { value: 'low', label: 'Under 10K messages/month' },
    { value: 'medium', label: '10K - 100K messages/month' },
    { value: 'high', label: '100K - 1M messages/month' },
    { value: 'enterprise', label: '1M+ messages/month' }
  ];

  const timelineOptions = [
    { value: 'immediate', label: 'Immediate (Within 1 month)' },
    { value: 'quarter', label: 'This Quarter (1-3 months)' },
    { value: 'half-year', label: 'Next 6 months' },
    { value: 'year', label: 'Within a year' },
    { value: 'exploring', label: 'Just exploring options' }
  ];

  const budgetOptions = [
    { value: 'under-1k', label: 'Under $1,000/month' },
    { value: '1k-5k', label: '$1,000 - $5,000/month' },
    { value: '5k-10k', label: '$5,000 - $10,000/month' },
    { value: '10k-25k', label: '$10,000 - $25,000/month' },
    { value: 'over-25k', label: 'Over $25,000/month' },
    { value: 'discuss', label: 'Prefer to discuss' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCheckboxChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev?.[field], value]
        : prev?.[field]?.filter(item => item !== value)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Invalid email format';
    if (!formData?.company?.trim()) newErrors.company = 'Company name is required';
    if (!formData?.jobTitle?.trim()) newErrors.jobTitle = 'Job title is required';
    if (!formData?.companySize) newErrors.companySize = 'Company size is required';
    if (!formData?.useCase) newErrors.useCase = 'Use case is required';
    if (!formData?.terms) newErrors.terms = 'You must agree to the terms';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-8 shadow-elevation-2">
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Get Started with MessagingHub Pro
        </h2>
        <p className="text-muted-foreground">
          Tell us about your project and we'll connect you with the right solutions and support.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            placeholder="John"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />
          <Input
            label="Last Name"
            type="text"
            placeholder="Doe"
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Work Email"
            type="email"
            placeholder="john@company.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            error={errors?.phone}
          />
        </div>

        {/* Company Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Company Name"
            type="text"
            placeholder="Acme Corporation"
            value={formData?.company}
            onChange={(e) => handleInputChange('company', e?.target?.value)}
            error={errors?.company}
            required
          />
          <Input
            label="Job Title"
            type="text"
            placeholder="CTO, Engineering Manager"
            value={formData?.jobTitle}
            onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
            error={errors?.jobTitle}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Company Size"
            placeholder="Select company size"
            options={companySizeOptions}
            value={formData?.companySize}
            onChange={(value) => handleInputChange('companySize', value)}
            error={errors?.companySize}
            required
          />
          <Select
            label="Industry"
            placeholder="Select your industry"
            options={industryOptions}
            value={formData?.industry}
            onChange={(value) => handleInputChange('industry', value)}
            searchable
          />
        </div>

        {/* Project Details */}
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Project Requirements</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Select
              label="Primary Use Case"
              placeholder="Select your main use case"
              options={useCaseOptions}
              value={formData?.useCase}
              onChange={(value) => handleInputChange('useCase', value)}
              error={errors?.useCase}
              required
            />
            <Select
              label="Expected Monthly Volume"
              placeholder="Select message volume"
              options={volumeOptions}
              value={formData?.monthlyVolume}
              onChange={(value) => handleInputChange('monthlyVolume', value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Select
              label="Implementation Timeline"
              placeholder="When do you need this?"
              options={timelineOptions}
              value={formData?.timeline}
              onChange={(value) => handleInputChange('timeline', value)}
            />
            <Select
              label="Budget Range"
              placeholder="Select budget range"
              options={budgetOptions}
              value={formData?.budget}
              onChange={(value) => handleInputChange('budget', value)}
            />
          </div>

          {/* Technical Requirements */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-3">
              Technical Requirements (Select all that apply)
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { value: 'real-time', label: 'Real-time messaging' },
                { value: 'file-sharing', label: 'File sharing & media' },
                { value: 'video-voice', label: 'Video & voice calling' },
                { value: 'push-notifications', label: 'Push notifications' },
                { value: 'offline-sync', label: 'Offline message sync' },
                { value: 'encryption', label: 'End-to-end encryption' },
                { value: 'moderation', label: 'Content moderation' },
                { value: 'analytics', label: 'Analytics & reporting' }
              ]?.map((req) => (
                <Checkbox
                  key={req?.value}
                  label={req?.label}
                  checked={formData?.technicalRequirements?.includes(req?.value)}
                  onChange={(e) => handleCheckboxChange('technicalRequirements', req?.value, e?.target?.checked)}
                />
              ))}
            </div>
          </div>

          {/* Compliance Needs */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-3">
              Compliance & Security Requirements
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { value: 'hipaa', label: 'HIPAA Compliance' },
                { value: 'gdpr', label: 'GDPR Compliance' },
                { value: 'soc2', label: 'SOC 2 Type II' },
                { value: 'iso27001', label: 'ISO 27001' },
                { value: 'pci', label: 'PCI DSS' },
                { value: 'ferpa', label: 'FERPA Compliance' }
              ]?.map((compliance) => (
                <Checkbox
                  key={compliance?.value}
                  label={compliance?.label}
                  checked={formData?.complianceNeeds?.includes(compliance?.value)}
                  onChange={(e) => handleCheckboxChange('complianceNeeds', compliance?.value, e?.target?.checked)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Additional Information
          </label>
          <textarea
            className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
            rows={4}
            placeholder="Tell us more about your project, specific requirements, or any questions you have..."
            value={formData?.message}
            onChange={(e) => handleInputChange('message', e?.target?.value)}
          />
        </div>

        {/* Agreements */}
        <div className="space-y-3">
          <Checkbox
            label="I agree to the Terms of Service and Privacy Policy"
            checked={formData?.terms}
            onChange={(e) => handleInputChange('terms', e?.target?.checked)}
            error={errors?.terms}
            required
          />
          <Checkbox
            label="I'd like to receive product updates and technical newsletters"
            checked={formData?.newsletter}
            onChange={(e) => handleInputChange('newsletter', e?.target?.checked)}
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isSubmitting}
            iconName="Send"
            iconPosition="right"
            className="shadow-elevation-2"
          >
            {isSubmitting ? 'Submitting Request...' : 'Submit Request'}
          </Button>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            We'll respond within 2 business hours during weekdays
          </p>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;